import { Badge, Loader, Text } from '@mantine/core';
import {
  IconArchive,
  IconMail,
  IconPencil,
  IconSignature,
  IconTrash,
  IconUserCheck,
  IconUserX,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import CreateUserModal from 'src/admin/users/components/CreateUserModal';
import { useUsersContext } from 'src/admin/users/context/UsersContext';
import { useClientPagination } from 'src/common/components/table/hooks/useClientPagination';
import { NameCell } from 'src/common/components/table/NameCell';
import { Table } from 'src/common/components/table/Table';
import { useDeleteUser } from '../hooks/useDeleteUser';
import { useResendInvite } from '../hooks/useResendInvite';
import { useUpdateUser } from '../hooks/useUpdateUser';
import useUsers from '../hooks/useUsers';
import { AvailableActions, User, UserRole, UserStatus } from '../types';
import { roleAsDisplayName, statusAsDisplayName } from '../utils';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import ActionMenu from './ActionMenu';
import { ActionMenuItemProps } from './ActionMenuItem';
import EditUserModal from './EditUserModal';
import { FilterRowDropdowns } from './FilterRow';
import SignatureModal from './SignatureModal';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import useCurrentUser from '../hooks/useCurrentUser';
import { useFlags } from 'src/feature-flags/FlagsHook';

const SIGNATURE_HOLDER_ROLES: UserRole[] = ['ADVISOR', 'SYSTEM_ADMIN'];

function getAvailableActions(user: User): AvailableActions[] {
  const actions: AvailableActions[] = ['edit'];

  switch (user.userStatus) {
    case 'ACTIVE':
      actions.push('deactivate');
      break;

    case 'DEACTIVATED':
      actions.push('reactivate');
      actions.push('archive');
      break;

    case 'PENDING':
      actions.push('deactivate');
      actions.push('resendInvite');
      break;

    case 'ARCHIVED':
      actions.push('unarchive');
      actions.push('delete');
      break;

    default:
      break;
  }
  return actions;
}

type UserTableProps = {
  title?: string;
  subtitle?: string;
  titleIcon?: React.ReactNode;
  onTitleClick?: () => void;
  filters?: Record<string, string>;
};

export function UsersTable({ titleIcon, onTitleClick }: UserTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<UserStatus | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [userForSignature, setUserForSignature] = useState<User | null>(null);
  const { flow: usersFlow, startUserCreation, close: usersClose } = useUsersContext();
  const { users, isLoading, refetch } = useUsers(['ADVISOR', 'SYSTEM_ADMIN', 'SERVICE_ADMIN']);
  const { isAfternoonAdmin } = useIsAfternoon();
  const { isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const { flagEnabledAdminAdviserSignatures } = useFlags();
  const canManageSignatures =
    flagEnabledAdminAdviserSignatures && (isSystemAdmin || isServiceAdmin);
  const {
    updateUser,
    error: updateUserError,
    isLoading: isUpdatingUser,
    reset: resetUpdateUser,
  } = useUpdateUser();
  const {
    deleteUser,
    error: deleteUserError,
    reset: resetDeleteUser,
    isLoading: isDeletingUser,
  } = useDeleteUser({});
  const { resendInvite } = useResendInvite();
  const navigate = useNavigate();

  const handleEditPress = (user: User) => {
    resetUpdateUser();
    setUserToEdit(user);
  };

  const handleEditConfirm = async (updatedUser: User) => {
    await updateUser(updatedUser.id, updatedUser);
    setUserToEdit(null);
    refetch();
  };

  const handleDeactivatePress = async (user: User) => {
    await updateUser(user.id, {
      userStatus: 'DEACTIVATED',
    });
    refetch();
  };

  const handleReactivatePress = async (user: User) => {
    await updateUser(user.id, {
      userStatus: 'ACTIVE',
    });
    refetch();
  };

  const handleArchivePress = async (user: User) => {
    await updateUser(user.id, {
      userStatus: 'ARCHIVED',
    });
    refetch();
  };

  const handleUnarchivePress = async (user: User) => {
    await updateUser(user.id, {
      userStatus: 'DEACTIVATED',
    });
    refetch();
  };

  const handleResendInvitePress = async (user: User) => {
    await resendInvite(user.id);
    refetch();
  };

  const handleDeletePress = (user: User) => {
    resetDeleteUser();
    setUserToDelete(user);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!userToDelete) return;
      await deleteUser(userToDelete.id);
      setUserToDelete(null);
      refetch();
    } catch (e) {
      setUserToDelete(null);
      notifyAndLogError(
        'Failed to delete user',
        'An error occurred while deleting the user. Please try again.',
        e,
        { userId: userToDelete?.id },
      );
    }
  };

  const renderName = (user: User) => {
    return <NameCell entity={user} />;
  };

  const renderStatus = (user: User) => {
    const status = user.userStatus as UserStatus;
    const statusColours: Record<UserStatus, string> = {
      ACTIVE: 'green',
      DEACTIVATED: 'gray',
      PENDING: 'orange',
      ARCHIVED: 'red',
    };
    const color = statusColours[status] || 'blue';
    return (
      <Badge color={color} variant="light">
        {statusAsDisplayName(status)}
      </Badge>
    );
  };

  const renderRole = (user: User) => {
    return (
      <Text size="sm" c="gray.7">
        {roleAsDisplayName(user.userRole)}
      </Text>
    );
  };

  const renderCreatedAt = (user: User) => {
    const date = new Date(user.createdAt);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    return (
      <Text size="sm" c="gray.7">
        {formattedDate}
      </Text>
    );
  };

  const renderActions = (user: User) => {
    const signatureItem: ActionMenuItemProps | null =
      canManageSignatures && SIGNATURE_HOLDER_ROLES.includes(user.userRole)
        ? {
            icon: <IconSignature size={16} />,
            label: 'Edit signature',
            onClick: () => setUserForSignature(user),
          }
        : null;

    if (!isSystemAdmin) {
      return <ActionMenu user={user} menuItems={signatureItem ? [signatureItem] : []} />;
    }

    const availableActions = getAvailableActions(user);
    const menuItems = availableActions
      .map(action => {
        switch (action) {
          case 'edit':
            return {
              icon: <IconPencil size={16} />,
              label: 'Edit user',
              onClick: () => handleEditPress(user),
            };
          case 'deactivate':
            return {
              icon: <IconUserX size={16} />,
              label: 'Deactivate user',
              onClick: () => handleDeactivatePress(user),
              disabled: ['DEACTIVATED', 'ARCHIVED'].includes(user.userStatus),
            };
          case 'reactivate':
            return {
              icon: <IconUserCheck size={16} />,
              label: 'Re-activate user',
              onClick: () => handleReactivatePress(user),
              disabled: user.userStatus !== 'DEACTIVATED',
            };
          case 'archive':
            return {
              icon: <IconArchive size={16} />,
              label: 'Archive user',
              onClick: () => handleArchivePress(user),
              disabled: user.userStatus !== 'DEACTIVATED',
            };
          case 'unarchive':
            return {
              icon: <IconArchive size={16} />,
              label: 'Unarchive user',
              onClick: () => handleUnarchivePress(user),
              disabled: user.userStatus !== 'ARCHIVED',
            };
          case 'resendInvite':
            return {
              icon: <IconMail size={16} />,
              label: 'Resend sign-up invitation',
              onClick: () => handleResendInvitePress(user),
              disabled: user.userStatus !== 'PENDING',
            };
          case 'delete':
            return {
              icon: <IconTrash size={16} />,
              label: 'Delete user',
              onClick: () => handleDeletePress(user),
              disabled: user.userStatus !== 'ARCHIVED',
            };
          default:
            return null;
        }
      })
      .filter(item => item !== null) as ActionMenuItemProps[];

    if (signatureItem) {
      menuItems.splice(1, 0, signatureItem);
    }

    return <ActionMenu user={user} menuItems={menuItems} />;
  };

  const columns = [
    {
      accessor: 'name',
      title: 'Name',
      sortable: true,
      sortKey: 'lastName',
      render: renderName,
    },
    {
      accessor: 'status',
      title: 'Status',
      sortable: true,
      sortKey: 'userStatus',
      render: renderStatus,
    },
    {
      accessor: 'role',
      title: 'Role',
      sortable: true,
      sortKey: 'userRole',
      render: renderRole,
    },
    {
      accessor: 'createdAt',
      title: 'Date added',
      sortable: true,
      sortKey: 'createdAt',
      render: renderCreatedAt,
    },
    {
      accessor: 'actions',
      title: '',
      render: renderActions,
    },
  ];

  const filterPredicate = (user: User, search: string) => {
    if (selectedStatus && user.userStatus !== selectedStatus) return false;
    if (selectedRole && user.userRole !== selectedRole) return false;

    if (search === '') return true;
    const searchableFields = [user.firstName, user.lastName, user.emailAddress];
    return searchableFields.some(field => field?.toLowerCase().includes(search.toLowerCase()));
  };

  const { paginatedData, pagination, totalRecords, sortStatus, onSortStatusChange } =
    useClientPagination({
      data: users,
      columns,
      searchTerm,
      filterPredicate,
      pageSize: 10,
    });

  if (isLoading) return <Loader size="xl" />;

  return (
    <>
      <CreateUserModal
        opened={usersFlow === 'create-user'}
        close={usersClose}
        showSecretMenuAccessOption={!!isAfternoonAdmin}
      />
      <Table<User>
        title="Users"
        titleIcon={titleIcon}
        onTitleClick={onTitleClick}
        columns={columns}
        data={paginatedData}
        isLoading={isLoading}
        variant="clients-table"
        onRowClick={user => {
          if (isSystemAdmin) navigate(`/admin/users/${user.id}`);
        }}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        totalRecords={totalRecords}
        pagination={pagination}
        sortStatus={sortStatus}
        onSortStatusChange={onSortStatusChange}
        HeaderRow={
          <FilterRowDropdowns
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
          />
        }
        showCount={false}
        primaryButton={
          isSystemAdmin
            ? {
                label: 'Add user',
                onClick: () => {
                  startUserCreation();
                },
              }
            : undefined
        }
      />
      <ConfirmDeleteModal
        opened={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete user"
        message={`Are you sure you want to delete ${userToDelete?.firstName} ${userToDelete?.lastName}? This action cannot be undone.`}
        error={deleteUserError}
        isLoading={isDeletingUser}
      />
      {userForSignature && (
        <SignatureModal user={userForSignature} onClose={() => setUserForSignature(null)} />
      )}
      {userToEdit && (
        <EditUserModal
          user={userToEdit}
          onClose={() => setUserToEdit(null)}
          onSave={handleEditConfirm}
          error={updateUserError}
          isLoading={isUpdatingUser}
          showSecretMenuAccessOption={false}
        />
      )}
    </>
  );
}
